import { CallQueueUsage } from '@weave/schema-gen-ts/dist/schemas/phone/callqueue-config/v1/callqueue_config_service.pb';
import { useQuery } from 'react-query';
import CallQueueAPI from '@frontend/api-call-queue';
import { Trans, useTranslation } from '@frontend/i18n';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { ModalControlModalProps, Text, useAlert, useModalControl } from '@frontend/design-system';
import { queryKeys } from '../../../query-keys';
import { trackingId } from '../../../tracking';
import { DeleteFlow } from '../../common/delete-flow';
import { CallQueueState } from './types';
import { useCallQueueMutations } from './use-call-queue-mutations';

export const DeleteCallQueueButton = ({
  callQueue,
  phoneTenantId,
}: {
  callQueue: CallQueueState;
  phoneTenantId: string;
}) => {
  const { t } = useTranslation('phone');

  const { modalProps, triggerProps } = useModalControl();

  return (
    <DeleteFlow.Button
      label={t('Delete Call Queue')}
      trackingId={trackingId({ context: 'setting', feature: 'call-queues', details: 'delete' })}
      triggerProps={triggerProps}
    >
      <CallQueueDeleteModal
        modalProps={modalProps}
        name={callQueue.name}
        id={callQueue.callQueueId}
        phoneTenantId={phoneTenantId}
      />
    </DeleteFlow.Button>
  );
};

export const DeleteCallQueueIconButton = ({
  name,
  id,
  phoneTenantId,
}: {
  name: string;
  id: string;
  phoneTenantId: string;
}) => {
  const { t } = useTranslation('phone');
  const { modalProps, triggerProps } = useModalControl();

  return (
    <DeleteFlow.IconButton
      label={t('Delete Call Queue')}
      triggerProps={triggerProps}
      trackingId={trackingId({ context: 'setting', feature: 'call-queues', details: 'index-delete' })}
    >
      <CallQueueDeleteModal modalProps={modalProps} name={name} id={id} phoneTenantId={phoneTenantId} />
    </DeleteFlow.IconButton>
  );
};

const CallQueueDeleteModal = ({
  modalProps,
  name,
  id,
  phoneTenantId,
}: {
  modalProps: ModalControlModalProps;
  name: string;
  id: string;
  phoneTenantId: string;
}) => {
  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: queryKeys.callQueueUsageV2(id),
    queryFn: () => CallQueueAPI.UsageV2({ callQueueId: id }),
    enabled: modalProps.show,
    staleTime: 0,
  });

  return (
    <DeleteFlow.Modal
      CanDeleteContent={CallQueueCanDeleteContent}
      CannotDeleteContent={CallQueueCannotDeleteContentV2}
      id={id}
      isLoading={isLoading}
      isFetching={isFetching}
      isError={isError}
      modalProps={modalProps}
      name={name}
      phoneTenantId={phoneTenantId}
      usage={data?.usages || []}
    />
  );
};

const CallQueueCanDeleteContent = ({
  modalProps,
  name,
  id,
  phoneTenantId,
}: {
  modalProps: ModalControlModalProps;
  name: string;
  id: string;
  phoneTenantId: string;
}) => {
  const { remove } = useCallQueueMutations(phoneTenantId);
  const { t } = useTranslation('phone');
  const alerts = useAlert();
  const { navigate } = useSettingsNavigate();

  const removeFn = () =>
    remove.mutate(
      { callQueueId: id },
      {
        onSuccess: () => {
          alerts.success(t('Call Queue deleted successfully.'));
          modalProps.onClose();
          navigate({ to: '/phone/call-queues' });
        },
        onError: () => {
          alerts.error(t('System failed to delete Call Queue. Please try again.'));
        },
      }
    );

  return (
    <DeleteFlow.CanDeleteContent
      modalProps={modalProps}
      name={name}
      title={t('Delete Call Queue?')}
      removeFn={removeFn}
    />
  );
};

const CallQueueCannotDeleteContentV2 = ({
  id,
  modalProps,
  name,
  usage,
  disableLinks = false,
}: {
  id: string;
  modalProps: ModalControlModalProps;
  name: string;
  usage: CallQueueUsage[];
  disableLinks?: boolean;
}) => {
  const { t } = useTranslation('phone');

  const usageCount = usage.length;

  return (
    <DeleteFlow.CannotDeleteContentV2
      id={id}
      showTable={true}
      modalProps={modalProps}
      disableLinks={disableLinks}
      usages={usage}
      title={t('Cannot Delete Call Queue')}
      body={
        <Trans t={t} name={name}>
          <Text>
            <Text as='span' weight='semibold'>
              {name}
            </Text>{' '}
            {usageCount === 1
              ? t(
                  'can’t be deleted because it is currently in use. The Call Queue must be removed from the following place in order to be deleted.'
                )
              : t(
                  'can’t be deleted because it is currently in use. The Call Queue must be removed from the following places in order to be deleted.'
                )}
          </Text>
        </Trans>
      }
    />
  );
};
